<script setup lang="ts">
import { RouteName } from "@/router";
import { invitationService } from "@/services/invitation-service";
import { authService } from "@/services/auth-service";
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  isCustomerAdminForAtLeastOneCustomer,
  isOneAdmin,
  isOneUserAdmin,
} from "@/utils/roles.util";

const INVITATIONS_COUNT_REFRESH_INTERVAL = 10000; // 10 seconds

const router = useRouter();
const activeRoute = useRoute();
const authenticated = ref(false);

async function onClickLogout() {
  await authService.logout();
  await router.push({ name: RouteName.Login });
}

watch(activeRoute, async () => {
  authenticated.value = authService.authenticated;
  await refreshInvitationsCount();
});

const loggedInUserDisplayName = computed(() => authService.loggedInUser?.name);
const loggedInUser = computed(() => authService.loggedInUser);

let invitationsCheckRefreshInterval: NodeJS.Timeout | undefined;
const invitationsCount = ref<number>(0);

async function refreshInvitationsCount() {
  if (!authService.authenticated) {
    return;
  }
  if (!isOneAdmin(loggedInUser.value)) return;

  const invitations = await invitationService.getPendingInvitations();
  invitationsCount.value = invitations.length;
}

onBeforeMount(async () => {
  authenticated.value = authService.authenticated;

  await refreshInvitationsCount();
  invitationsCheckRefreshInterval = setInterval(
    refreshInvitationsCount,
    INVITATIONS_COUNT_REFRESH_INTERVAL,
  );
});

onBeforeUnmount(() => {
  clearInterval(invitationsCheckRefreshInterval);
});

const routes = computed(() => [
  { text: "Hem", name: RouteName.Root, visible: () => true },
  {
    text: "Inbjudningar",
    name: RouteName.Invites,
    showBadge: invitationsCount.value > 0,
    badgeCount: invitationsCount,
    visible: () => isOneAdmin(loggedInUser.value),
  },
  {
    text: "Kundtjänst",
    name: RouteName.CustomerService,
    visible: () =>
      isOneAdmin(loggedInUser.value) || isOneUserAdmin(loggedInUser.value),
  },
  {
    text: "Serviceärende",
    name: RouteName.ServiceOrders,
    visible: () =>
      isOneAdmin(loggedInUser.value) ||
      isCustomerAdminForAtLeastOneCustomer(loggedInUser.value),
  },
  {
    text: `Profil (${loggedInUserDisplayName.value})`,
    name: RouteName.Profile,
    visible: () => true,
  },
  {
    text: "Entra",
    name: RouteName.Entra,
    visible: () => isOneAdmin(loggedInUser.value),
  },
]);

const isHidden = computed(
  () =>
    [RouteName.ErrorReportsCreate].includes(activeRoute.name as RouteName) ||
    [RouteName.Login].includes(activeRoute.name as RouteName),
);

const visibleRoutes = computed(() => routes.value.filter((r) => r.visible()));
</script>

<template>
  <div v-if="!isHidden" class="container">
    <section class="hero is-small">
      <div class="hero-body px-4 pb-0 columns">
        <div class="column">
          <figure>
            <img src="@/assets/logo.png" alt="One Nordic" class="mr-4 logo" />
          </figure>
          <p class="title">Kundportal</p>
          <button
            v-if="authenticated"
            class="button is-dark is-inverted mr-2"
            @click="router.go(-1)"
          >
            Tillbaka
          </button>
        </div>
        <div
          v-if="authenticated"
          class="column is-flex is-align-items-flex-end is-justify-content-flex-end"
        >
          <router-link
            v-for="route in visibleRoutes"
            :key="route.name"
            :to="{ name: route.name }"
            class="button is-light mx-2 is-relative"
          >
            {{ route.text }}

            <span v-if="route.showBadge" class="badge tag is-danger is-dark">
              {{ route.badgeCount }}
            </span>
          </router-link>

          <!--            class="button is-dark is-inverted mx-2"-->
          <a class="button is-dark is-inverted" @click="onClickLogout"
            >Logga ut</a
          >
        </div>
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.logo {
  max-height: 6rem;
}

.badge {
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(30%, -30%);
  font-weight: 700;
  font-size: 12px;
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--bulma-danger);
  color: white;
  border-radius: 50%;
  text-indent: -1px;
}
</style>
